import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import * as courseService from '../../services/course.service';
import * as lessonCommentService from '../../services/lessonComment.service';
import CommentSection from '../../components/common/CommentSection';
import { formatDuration } from '../../utils/duration';
import { renderRichText as renderTranscript } from '../../utils/richText';

export default function Lesson() {
  const { courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [course, setCourse]   = useState(null); // course detail w/ lesson list, for the sidebar
  const [lesson, setLesson]   = useState(null); // full single-lesson content
  const [error, setError]     = useState(null);
  const [completing, setCompleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      setLesson(null);
      const [courseData, lessonData] = await Promise.all([
        courseService.getCourseDetail(courseId),
        courseService.getLesson(courseId, lessonId),
      ]);
      setCourse(courseData);
      setLesson(lessonData);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this lesson.');
    }
  }, [courseId, lessonId]);

  useEffect(() => { load(); }, [load]);

  const getLessonComments = useCallback(() => lessonCommentService.getComments(courseId, lessonId), [courseId, lessonId]);
  const createLessonComment = useCallback((body, parentCommentId, imageUrl) => lessonCommentService.createComment(courseId, lessonId, body, parentCommentId, imageUrl), [courseId, lessonId]);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await courseService.completeLesson(courseId, lessonId);
      const refreshed = await courseService.getCourseDetail(courseId);
      setCourse(refreshed);

      const currentIndex = refreshed.lessons.findIndex(l => l.id === lessonId);
      const next = refreshed.lessons[currentIndex + 1];
      if (next && !next.locked) {
        navigate(`/dashboard/learning-lab/${courseId}/${next.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save your progress.');
    } finally {
      setCompleting(false);
    }
  };

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-700">
          {error}
          <button onClick={() => navigate('/dashboard/learning-lab')} className="block mt-2 underline">
            Back to Learning Lab
          </button>
        </div>
      </DashboardLayout>
    );
  }

  if (!course || !lesson) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto text-sm text-gray-400">Loading lesson…</div>
      </DashboardLayout>
    );
  }

  const completedCount = course.lessons.filter(l => l.completed).length;
  const coursePct = course.lessons.length > 0
    ? Math.round((completedCount / course.lessons.length) * 100)
    : 0;
  const currentState = course.lessons.find(l => l.id === lessonId);
  const initials = (user?.name || '?').trim().charAt(0).toUpperCase();

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-6">

        {/* ── Sidebar: avatar, course progress, lecture list ── */}
        <aside className="lg:w-72 shrink-0 order-2 lg:order-1">
          <div className="bg-white rounded-2xl shadow-sm p-4 sticky top-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-reset-terracotta text-white flex items-center justify-center text-sm font-bold shrink-0">
                {initials}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-reset-brown truncate">{course.title}</p>
                {course.instructor_name && <p className="text-xs text-reset-sage truncate">with {course.instructor_name}</p>}
                <p className="text-xs text-gray-400">{coursePct}% complete</p>
              </div>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-4">
              <div className="h-full bg-reset-terracotta rounded-full transition-all" style={{ width: `${coursePct}%` }} />
            </div>

            <nav className="space-y-1 max-h-[60vh] overflow-y-auto">
              {course.lessons.map((l, i) => {
                const isCurrent = l.id === lessonId;
                const showSectionHeader = l.section_title && l.section_title !== course.lessons[i - 1]?.section_title;
                return (
                  <React.Fragment key={l.id}>
                    {showSectionHeader && (
                      <p className={`text-[10px] font-semibold text-gray-400 uppercase tracking-wide px-3 ${i === 0 ? 'pt-0' : 'pt-3'} pb-1`}>
                        {l.section_title}
                      </p>
                    )}
                    <button
                      disabled={l.locked}
                      onClick={() => navigate(`/dashboard/learning-lab/${courseId}/${l.id}`)}
                      className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        isCurrent
                          ? 'bg-reset-terracotta/10 text-reset-brown font-semibold'
                          : l.locked
                            ? 'text-gray-300 cursor-not-allowed'
                            : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <span className="w-4 text-xs shrink-0">
                        {l.completed ? '✅' : l.locked ? '🔒' : i + 1}
                      </span>
                      {l.resolved_image_url && (
                        <img
                          src={l.resolved_image_url} alt={l.resolved_image_alt_text || ''}
                          className="w-14 aspect-video rounded-md object-cover shrink-0"
                        />
                      )}
                      <span className="truncate flex-1">{l.title}</span>
                      {l.video_duration_seconds > 0 && (
                        <span className="text-xs text-gray-400 shrink-0">{formatDuration(l.video_duration_seconds)}</span>
                      )}
                    </button>
                  </React.Fragment>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* ── Main lesson content ── */}
        <div className="flex-1 order-1 lg:order-2 min-w-0">
          <button
            onClick={() => navigate('/dashboard/learning-lab')}
            className="text-xs text-gray-400 hover:text-reset-brown mb-3"
          >
            ← Learning Lab
          </button>

          {/* Video — Bunny preferred over Vimeo (2026-08-19: consolidating course video hosting onto Bunny) */}
          {lesson.video_bunny_embed_url ? (
            <div className="rounded-2xl overflow-hidden shadow-sm bg-black aspect-video mb-5">
              <iframe
                key={lesson.id}
                src={lesson.video_bunny_embed_url}
                title={lesson.title}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : lesson.video_vimeo_id ? (
            <div className="rounded-2xl overflow-hidden shadow-sm bg-black aspect-video mb-5">
              <iframe
                key={lesson.id}
                src={`https://player.vimeo.com/video/${lesson.video_vimeo_id}${lesson.video_vimeo_hash ? `?h=${lesson.video_vimeo_hash}` : ''}`}
                title={lesson.title}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : lesson.resolved_image_url ? (
            <div className="rounded-2xl overflow-hidden shadow-sm aspect-video mb-5">
              <img src={lesson.resolved_image_url} alt={lesson.resolved_image_alt_text || ''} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="rounded-2xl bg-gray-100 aspect-video mb-5 flex items-center justify-center text-sm text-gray-400">
              Video coming soon
            </div>
          )}

          <h1 className="font-serif text-xl font-bold text-reset-brown mb-1">{lesson.title}</h1>
          {lesson.audio_url && (
            <audio controls preload="none" src={lesson.audio_url} className="w-full my-3" aria-label={`Audio for ${lesson.title}`} />
          )}
          {(lesson.instructor_name || course.instructor_name) && (
            <p className="text-sm font-semibold text-reset-sage mb-1">with {lesson.instructor_name || course.instructor_name}</p>
          )}
          {lesson.video_duration_seconds > 0 && (
            <p className="text-xs text-gray-400 mb-5">{formatDuration(lesson.video_duration_seconds)}</p>
          )}

          {lesson.transcript && (
            <details open className="bg-white rounded-2xl shadow-sm p-4 mb-4">
              <summary className="text-sm font-semibold text-reset-brown cursor-pointer">Lesson</summary>
              <div className="text-sm text-gray-600 leading-relaxed mt-3">
                {renderTranscript(lesson.transcript)}
              </div>
            </details>
          )}

          {lesson.exercise_file_url && (
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
              <h2 className="text-sm font-semibold text-reset-brown mb-2">Exercise</h2>
              <a
                href={lesson.exercise_file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-reset-terracotta font-medium hover:underline"
              >
                Open exercise ↗
              </a>
            </div>
          )}

          {lesson.resource_links?.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm p-4 mb-4">
              <h2 className="text-sm font-semibold text-reset-brown mb-2">Recommended Resources</h2>
              <ul className="space-y-1.5">
                {lesson.resource_links.map((res, i) => {
                  const url = typeof res === 'string' ? res : res.url;
                  const label = typeof res === 'string' ? res : (res.title || res.label || res.url);
                  return (
                    <li key={i}>
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-reset-terracotta hover:underline"
                      >
                        {label} ↗
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <button
            onClick={handleComplete}
            disabled={completing || currentState?.completed}
            className={`w-full sm:w-auto px-6 py-3 rounded-xl text-sm font-medium transition-colors ${
              currentState?.completed
                ? 'bg-green-50 text-green-700 cursor-default'
                : 'bg-reset-terracotta text-white hover:bg-reset-brown'
            }`}
          >
            {currentState?.completed ? '✅ Lesson complete' : completing ? 'Saving…' : 'Mark lesson complete'}
          </button>

          <CommentSection
            getComments={getLessonComments}
            createComment={createLessonComment}
            deleteOwnComment={lessonCommentService.deleteOwnComment}
            currentUserId={user?.id}
          />
        </div>

      </div>
    </DashboardLayout>
  );
}
